/**
 * Load something, once, and keep the three states apart.
 *
 * Loading, failed and empty are DIFFERENT, and collapsing them is the most
 * common way a console lies: a failed request that renders as an empty list
 * tells a streamer their catalog is gone.
 *
 * Aborts on unmount and on a changed key, so a slow first request cannot land
 * after a fast second one and overwrite it.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from './lib/api.js';

export type Async<T> =
  | { state: 'loading' }
  | { state: 'ready'; value: T }
  | { state: 'failed'; error: ApiError };

export function useAsync<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[]
): [Async<T>, () => void] {
  const [result, setResult] = useState<Async<T>>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => { setAttempt((n) => n + 1); }, []);

  useEffect(() => {
    const controller = new AbortController();
    setResult({ state: 'loading' });

    load(controller.signal).then(
      (value) => { if (!controller.signal.aborted) setResult({ state: 'ready', value }); },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        setResult({
          state: 'failed',
          error: error instanceof ApiError
            ? error
            // Anything that is not an ApiError escaped the one place that was
            // supposed to translate failures. Surfacing it as "unknown" beats
            // rendering an empty screen.
            : new ApiError('malformed_response', 0, 'Something unexpected went wrong.'),
        });
      }
    );

    return () => { controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt]);

  return [result, retry];
}
